import { HttpClient } from '@angular/common/http';
import { Component, OnInit, ChangeDetectorRef, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import * as XLSX from 'xlsx';
import moment from 'moment';
import { UserDashboardService } from 'src/app/services/user-dashboard.service';
import { StorageService } from 'src/app/services/storage.service';
import { UserManagementService } from 'src/app/services/user-management.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { EstampService } from 'src/app/services/estamp.service';

import {
  ExcelRow,
  ExcelValidationResult,
  EstampPayloadItem,
  EsignPartyUser,
  EsignPayloadBlock,
  ExcelDataItem,
  PresignedUrlRequest,
  PresignedUrlApiResponse,
  TransformedPresignedUrl,
  TransformedPresignedUrlsData,
  ConsolidatedError,
  ValidationError,
} from '../bulk-upload.interfaces';
import { BothValidatorService } from 'src/app/services/both-validator.service';
import { EsignValidatorService } from 'src/app/services/esign-validator.service';
import { EstampValidatorService } from 'src/app/services/estamp-validatior.service';

@Component({
  selector: 'app-bulk-upload-excel',
  templateUrl: './bulk-upload-excel.component.html',
  styleUrls: ['./bulk-upload-excel.component.scss'],
  standalone: false,
})
export class BulkUploadExcelComponent implements OnInit {
  showUploadModal = false;
  isValidating = false;
  isProcessing = false;
  isFileConfirmed = false;
  uploadedExcelFile: File | null = null;
  excelFileName = '';
  selectedServiceType: 'eStamp' | 'eSign' | 'both' | '' = '';
  validationResult: ExcelValidationResult | null = null;
  consolidatedErrors: ConsolidatedError[] = [];
  fileNameDuplicateErrors: ValidationError[] = [];
  rowsWithIssues: ExcelRow[] = [];
  validRowCount = 0;
  issuesSkipped = false;
  apiInlineError: string | null = null;

  convertedData: ExcelRow[] = [];
  originalConvertedData: ExcelRow[] = [];

  totalStampAmount = 0;
  totalStampAmountForPopup = 0;
  stampCount = 0;
  total_order = 0;

  maxRows = 5000;

  selectedStampProcessType = 'PRE_PROCUREMENT';

  branches: string[] = [];
  branchList: any[] = [];
  userBranchList: any[] = [];
  userId: any;
  userProfileData: any;
  showTemplateDropdown = false;

  // ── FIX: plain cached property instead of getter to avoid CD freeze ──
  errorTableColumns: { header: string; field: string }[] = [];

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private userDashboardService: UserDashboardService,
    private storageService: StorageService,
    private userManagementService: UserManagementService,
    private modalService: NgbModal,
    private estampService: EstampService,
    private estampValidator: EstampValidatorService,
    private esignValidator: EsignValidatorService,
    private bothValidator: BothValidatorService,
  ) {}

  ngOnInit(): void {
    sessionStorage.removeItem('excelValidationData');
    sessionStorage.removeItem('uploadUrlsData');

    this.userId = this.storageService.getUserID() || null;
    if (this.userId) this.editUserDetails(this.userId);

    // ── FIX: compute columns on init ──
    this._updateErrorTableColumns();

    setTimeout(() => {
      this.userDashboardService.getBranchList().subscribe((res: any) => {
        this.branchList = (res['content'] as any[]).filter(
          (e) => e.is_active === true,
        );
        this.branchList.forEach((e) => this.userBranchList.push(e.branch_code));
      });
    }, 1000);

    const saved = sessionStorage.getItem('excelValidationData');
    if (saved) {
      try {
        this.validationResult = JSON.parse(saved);
        if (this.validationResult?.isValid) {
          this.isFileConfirmed = true;
          this.excelFileName = 'bulk_upload.xlsx';
        }
      } catch {
        /* ignore */
      }
    }
  }

  editUserDetails(id: any) {
    this.userManagementService.getUsersDetailsById(id).subscribe((res: any) => {
      this.userProfileData = res['content'][0];
      this.branches = this.userProfileData?.branch_codes ?? [];
    });
  }

  onServiceTypeChange(value: string) {
    this.uploadedExcelFile = null;
    this.excelFileName = '';
    this.validationResult = null;
    this.isFileConfirmed = false;
    this.apiInlineError = null;
    this.resetValidationState();
    sessionStorage.removeItem('excelValidationData');
    // ── FIX: recompute columns when service type changes ──
    this._updateErrorTableColumns();
  }

  openUploadModal() {
    if (this.apiInlineError) {
      this.uploadedExcelFile = null;
      this.excelFileName = '';
      this.validationResult = null;
      this.isFileConfirmed = false;
      this.apiInlineError = null;
      this.resetValidationState();
      sessionStorage.removeItem('excelValidationData');
    }
    this.showUploadModal = true;
  }

  closeUploadModal() {
    this.showUploadModal = false;
    if (!this.isFileConfirmed) {
      this.uploadedExcelFile = null;
      this.excelFileName = '';
      this.validationResult = null;
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) this.handleFileSelection(files[0]);
  }

  onExcelFileSelect(event: any) {
    const file: File = event.target.files[0];
    if (file) this.handleFileSelection(file);
    event.target.value = '';
  }

  private handleFileSelection(file: File) {
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];
    if (!validTypes.includes(file.type)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Invalid File Type',
        detail: 'Please upload a valid Excel file (.xlsx or .xls)',
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.messageService.add({
        severity: 'error',
        summary: 'File Too Large',
        detail: 'Excel file size should not exceed 10 MB',
      });
      return;
    }
    this.uploadedExcelFile = file;
    this.excelFileName = file.name;
    this.validationResult = null;
    this.apiInlineError = null;
    this.validateExcel();
  }

  removeExcelFile() {
    this.uploadedExcelFile = null;
    this.excelFileName = '';
    this.validationResult = null;
    this.apiInlineError = null;
    this.resetValidationState();
  }

  removeConfirmedFile() {
    if (!confirm('Are you sure you want to remove the uploaded Excel file?'))
      return;
    this.uploadedExcelFile = null;
    this.excelFileName = '';
    this.validationResult = null;
    this.isFileConfirmed = false;
    this.apiInlineError = null;
    this.resetValidationState();
    sessionStorage.removeItem('excelValidationData');
    this.messageService.add({
      severity: 'info',
      summary: 'File Removed',
      detail: 'Please upload again.',
    });
  }

  private resetValidationState() {
    this.consolidatedErrors = [];
    this.fileNameDuplicateErrors = [];
    this.rowsWithIssues = [];
    this.validRowCount = 0;
    this.issuesSkipped = false;
    this.convertedData = [];
    this.originalConvertedData = [];
    this.totalStampAmount = 0;
    this.totalStampAmountForPopup = 0;
    this.stampCount = 0;
    this.total_order = 0;
  }

  async validateExcel() {
    if (!this.uploadedExcelFile) return;

    this.isValidating = true;
    this.resetValidationState();

    try {
      const rawData = await this.readExcelFile(this.uploadedExcelFile);

      if (!rawData || rawData.length === 0) {
        this._setFatalError('Excel file is empty.');
        return;
      }
      if (rawData.length > this.maxRows) {
        this._setFatalError(
          `Excel contains ${rawData.length} rows. Maximum allowed is ${this.maxRows}.`,
        );
        return;
      }

      const missingCol = this._checkRequiredColumns(rawData[0]);
      if (missingCol) {
        this._setFatalError(missingCol);
        return;
      }

      this.convertedData = this._processRawRows(rawData);
      this.originalConvertedData = [...this.convertedData];

      // Account ID check for all service types
      if (
        this.selectedServiceType === 'eStamp' ||
        this.selectedServiceType === 'eSign' ||
        this.selectedServiceType === 'both'
      ) {
        const excelAccountId = String(
          this.convertedData[0]?.account_id ?? this.storageService.getAccountId(),
        ).trim();
        const dashboardAccountId = String(
          this.storageService.getAccountId() ?? '',
        ).trim();

        if (!excelAccountId) {
          this._setFatalError(
            'Account ID is missing in the uploaded Excel file.',
          );
          return;
        }

        if (excelAccountId !== dashboardAccountId) {
          this._setFatalError(
            `Account ID mismatch: Excel contains account "${excelAccountId}" but your dashboard has account "${dashboardAccountId}" selected. Please select the correct account or fix the Excel file.`,
          );
          return;
        }
      }

      let result: {
        isValid: boolean;
        consolidatedErrors: ConsolidatedError[];
        rowsWithIssues: ExcelRow[];
        validRowCount: number;
        fileNameDuplicateErrors: ValidationError[];
      };

      const refIdFileNameValidation = this.storageService.getRefIdWithFileNameValidation();

      if (this.selectedServiceType === 'eStamp') {
        result = this.estampValidator.validate(
          this.convertedData,
          this.branches,
          refIdFileNameValidation,
        );
      } else if (this.selectedServiceType === 'eSign') {
        result = this.esignValidator.validate(
          this.convertedData,
          this.branches,
          refIdFileNameValidation,
        );
      } else {
        result = this.bothValidator.validate(this.convertedData, this.branches, refIdFileNameValidation);
      }

      this.consolidatedErrors = result.consolidatedErrors;
      this.rowsWithIssues = result.rowsWithIssues;
      this.validRowCount = result.validRowCount;
      this.fileNameDuplicateErrors = result.fileNameDuplicateErrors;

      this._computeTotals();

      const uniqueFileNames = [
        ...new Set(this.convertedData.map((r) => r.file_name).filter(Boolean)),
      ] as string[];

      this.validationResult = {
        isValid: result.isValid,
        errors: result.consolidatedErrors.map((e) => e.displayText),
        fileNames: uniqueFileNames,
        fileCount: uniqueFileNames.length,
        data: this.convertedData,
      };

      if (result.isValid) {
        this.messageService.add({
          severity: 'success',
          summary: 'Validation Successful',
          detail: `${uniqueFileNames.length} unique PDF file(s) found.`,
        });
      } else {
        this.messageService.add({
          severity: 'error',
          summary: 'Validation Failed',
          detail: `${result.consolidatedErrors.length} error(s) found.`,
        });
      }
    } catch (err: any) {
      this._setFatalError(err.message || 'Failed to validate Excel file.');
    } finally {
      this.isValidating = false;
      this.cdr.detectChanges();
    }
  }

  skipAndContinue() {
    const errorRows = new Set<number>(
      this.consolidatedErrors
        .map((e) => e.originalItem?.rowNumber)
        .filter((n): n is number => n !== undefined),
    );

    this.convertedData = this.convertedData.filter(
      (_, i) => !errorRows.has(i + 2),
    );

    const uniqueFileNames = [
      ...new Set(this.convertedData.map((r) => r.file_name).filter(Boolean)),
    ] as string[];
    this.validRowCount = this.convertedData.length;

    this.consolidatedErrors = [];
    this.fileNameDuplicateErrors = [];
    this.rowsWithIssues = [];
    this.issuesSkipped = true;

    this._computeTotals();

    this.validationResult = {
      isValid: true,
      errors: [],
      fileNames: uniqueFileNames,
      fileCount: uniqueFileNames.length,
      data: this.convertedData,
    };

    this.messageService.add({
      severity: 'success',
      summary: 'Errors Skipped',
      detail: `Proceeding with ${this.validRowCount} valid rows.`,
    });
    this.cdr.detectChanges();
  }

  confirmUpload() {
    if (!this.validationResult?.isValid) return;
    sessionStorage.setItem(
      'excelValidationData',
      JSON.stringify(this.validationResult),
    );
    sessionStorage.setItem('selectedServiceType', this.selectedServiceType);
    this.isFileConfirmed = true;
    this.closeUploadModal();
    this.messageService.add({
      severity: 'success',
      summary: 'File Confirmed',
      detail: 'Click Next to proceed.',
    });
  }

  async proceedToUpload() {
    this.apiInlineError = null;

    if (!this.validationResult?.isValid || !this.isFileConfirmed) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Ready',
        detail: 'Please upload and validate the Excel file first.',
      });
      return;
    }
    if (!this.convertedData.length) {
      this.messageService.add({
        severity: 'error',
        summary: 'No Data',
        detail: 'No valid Excel data found.',
      });
      return;
    }

    this.isProcessing = true;

    try {
      const payload = this._buildPayload();
      const sessionUuid = this._generateSessionUuid();
      const apiResponse = await this._callPresignedUrlApi(payload, sessionUuid);

      if (apiResponse?.status === 'error') {
        throw new Error(
          apiResponse.detail || apiResponse.message || 'Failed to generate upload URLs.',
        );
      }

      if (!apiResponse?.bulk_order_uuid || !apiResponse.urls)
        throw new Error('Invalid API response format.');

      const transformed = this._transformApiResponse(apiResponse);
      sessionStorage.setItem(
        'excelValidationData',
        JSON.stringify(this.validationResult),
      );
      sessionStorage.setItem('presignedUrlsData', JSON.stringify(transformed));
      sessionStorage.setItem('sessionUuid', sessionUuid);

      this.messageService.add({
        severity: 'success',
        summary: 'URLs Retrieved',
        detail: `Ready to upload ${apiResponse.urls.length} PDF file(s).`,
      });

      setTimeout(
        () => this.router.navigate(['/doqfy/bulk-upload-estamp/pdf-upload']),
        500,
      );
    } catch (error: any) {
      const detail =
        error?.error?.detail ||
        (typeof error?.error === 'string' ? error.error : null) ||
        error?.message ||
        'An error occurred.';
      this.apiInlineError = detail;
      this.isFileConfirmed = false;
      sessionStorage.removeItem('excelValidationData');
      this.cdr.detectChanges();
    } finally {
      this.isProcessing = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Routes to the correct payload builder based on selectedServiceType.
   */
  private _buildPayload(): PresignedUrlRequest {
    if (this.selectedServiceType === 'eStamp')
      return this._buildEstampPayload();
    if (this.selectedServiceType === 'eSign') return this._buildEsignPayload();
    return this._buildBothPayload();
  }

  private _buildEstampPayload(): PresignedUrlRequest {
    const fileMap = this._groupByFileName(this.convertedData);
    const excelData: ExcelDataItem[] = [];

    fileMap.forEach((rows, fileName) => {
      const estamps: EstampPayloadItem[] = rows.map((r) => ({
        first_party_name: r.first_party_name ?? '',
        second_party_name: r.second_party_name ?? '',
        stamp_duty_paid_by: r.stamp_duty_paid_by ?? '',
        article_code: r.article_code ?? '',
        stamp_duty_amount: Number(r.stamp_duty_amount ?? 0),
        consideration_amount: Number(r.consideration_amount ?? 0),
        description: r.description ?? '',
        execution_date: this._toTimestamp(r.execution_date),
        stamp_type: r.stamp_type,
      }));

      excelData.push({
        file_name: fileName,
        referance_id:
          rows.find((r) => r.referance_id)?.referance_id.toString() ?? null,
        estamps,
      });
    });

    return this._buildBasePayload(excelData);
  }

  private _buildEsignPayload(): PresignedUrlRequest {
    const fileMap = this._groupByFileName(this.convertedData);
    const excelData: ExcelDataItem[] = [];

    fileMap.forEach((rows, fileName) => {
      const partyUsers: EsignPartyUser[] = rows.map((r, idx) =>
        this._buildPartyUser(r, idx),
      );

      const esigns: EsignPayloadBlock = {
        party_users: partyUsers,
        witness_users: [],
      };

      excelData.push({
        file_name: fileName,
        referance_id:
          rows.find((r) => r.referance_id)?.referance_id.toString() ?? null,
        estamps: [],
        esigns,
      });
    });

    return this._buildBasePayload(excelData);
  }

  private _buildBothPayload(): PresignedUrlRequest {
  const fileMap   = this._groupByFileName(this.convertedData);
  const excelData: ExcelDataItem[] = [];

  fileMap.forEach((rows, fileName) => {
    // ── Stamp from FIRST row only — signatory rows have no stamp fields ──
    const firstRow = rows[0];
    const estamps: EstampPayloadItem[] = [{
      first_party_name:    firstRow.first_party_name    ?? '',
      second_party_name:   firstRow.second_party_name   ?? '',
      stamp_duty_paid_by:  firstRow.stamp_duty_paid_by  ?? '',
      article_code:        firstRow.article_code        ?? '',
      stamp_duty_amount:   Number(firstRow.stamp_duty_amount   ?? 0),
      consideration_amount: Number(firstRow.consideration_amount ?? 0),
      description:         firstRow.description         ?? '',
      execution_date:      this._toTimestamp(firstRow.execution_date),
      stamp_type:          firstRow.stamp_type,
    }];

    // ── Signatories: all rows ──
    const partyUsers: EsignPartyUser[] = rows.map((r, idx) =>
      this._buildPartyUser(r, idx),
    );

    const esigns: EsignPayloadBlock = {
      party_users:   partyUsers,
      witness_users: [],
    };

    excelData.push({
      file_name:    fileName,
      referance_id: rows.find((r) => r.referance_id)?.referance_id.toString() ?? null,
      estamps,
      esigns,
    });
  });

  return this._buildBasePayload(excelData);
}

  private _buildBasePayload(excelData: ExcelDataItem[]): PresignedUrlRequest {
    const firstRow = this.convertedData[0];
    return {
      org_id: this.storageService.getOrganizationId(),
      group_id: this.storageService.getSqsGroupID(),
      account_id: Number(firstRow?.account_id ?? this.storageService.getAccountId()),
      branch_code: firstRow?.branch_code ?? '',
      excel_data: excelData,
    };
  }

  private _buildPartyUser(row: ExcelRow, sequence: number): EsignPartyUser {
    return {
      name: row.name ?? '',
      email: row.email ?? '',
      contact_number: row.contact_number?.toString() ?? '',
      sign_position: row.sign_position ?? '',
      method: row.method ?? '',
      pages: row.pages ?? '',
      esign_otp: 'esign_otp' in row ? row.esign_otp : false,
      remark: '',
      reminder: 1,
      send_document: true,
      send_notification: false,
      sign_mode: 'sign_mode' in row ? row.sign_mode : '',
      aadhaar_digits: 'aadhaar_digits' in row ? row.aadhaar_digits : 0,
    };
  }

  /** Group ExcelRow[] by file_name */
  private _groupByFileName(rows: ExcelRow[]): Map<string, ExcelRow[]> {
    const map = new Map<string, ExcelRow[]>();
    rows.forEach((row) => {
      const key = row.file_name;
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(row);
    });
    return map;
  }

  /** Check that all required columns are present for the chosen service type */
  private _checkRequiredColumns(firstRow: any): string | null {
    let required: string[];
    if (this.selectedServiceType === 'eStamp') {
      required = this.estampValidator.requiredColumns;
    } else if (this.selectedServiceType === 'eSign') {
      required = this.esignValidator.requiredColumns;
    } else {
      required = this.bothValidator.requiredColumns;
    }
    const available = Object.keys(firstRow);
    const missing = required.filter((c) => !available.includes(c));
    return missing.length > 0
      ? `Missing required column(s): ${missing.join(', ')}`
      : null;
  }

  /** Parse raw XLSX rows into clean ExcelRow objects */
  private _processRawRows(data: any[]): ExcelRow[] {
    const cleaned = data
      .map((obj) => {
        const clean: any = {};
        for (const key in obj) {
          const v = obj[key];
          if (v === '' || v === null || v === undefined) continue;
          if (typeof v === 'number' || v instanceof Date) { clean[key] = v; continue; }
          if (typeof v === 'string' && v.trim() !== '') { clean[key] = v.trim(); continue; }
          clean[key] = v;
        }
        return clean;
      })
      .filter((obj) => Object.keys(obj).length > 0);

    const inheritFields = ['referance_id', 'account_id', 'branch_code', 'file_name'];
    let currentGroupValues: Record<string, any> = {};
    const seenRefIds = new Set<string>(); // ← track already-seen reference IDs

    return cleaned.map((row) => {
      const refId = row['referance_id']?.toString().trim() ?? '';
      const isNewGroup = refId !== '' && !seenRefIds.has(refId); // ← first occurrence only

      if (isNewGroup) {
        seenRefIds.add(refId);
        currentGroupValues = {};
        inheritFields.forEach((f) => {
          if (row[f] !== undefined && row[f] !== '') currentGroupValues[f] = row[f];
        });
        return row;
      }

      // Signatory-only row OR repeated ref_id row — inherit missing fields from current group
      const enriched = { ...row };
      inheritFields.forEach((f) => {
        if ((enriched[f] === undefined || enriched[f] === '') && currentGroupValues[f] !== undefined) {
          enriched[f] = currentGroupValues[f];
        }
      });
      return enriched;
    });
  }

  /** Convert any date representation to Unix ms timestamp */
  private _toTimestamp(val: any): number {
    if (!val) return Date.now();
    if (typeof val === 'number') return val;
    if (val instanceof Date) return val.getTime();
    if (typeof val === 'string') {
      const parsed = moment(val, ['DD-MM-YYYY', 'D-M-YYYY'], true);
      if (parsed.isValid()) return parsed.valueOf();
    }
    return Date.now();
  }

  /** Compute stamp totals for modal summary */
  private _computeTotals() {
    this.totalStampAmount = 0;
    this.totalStampAmountForPopup = 0;
    this.stampCount = 0;
    this.total_order = 0;
    const preProcurement = this.selectedStampProcessType === 'PRE_PROCUREMENT';

    this.convertedData.forEach((row) => {
      if (row.referance_id) this.total_order++;
      const amt =
        typeof row.stamp_duty_amount === 'string'
          ? parseFloat(row.stamp_duty_amount)
          : row.stamp_duty_amount;
      if (amt && !isNaN(amt) && amt > 0) {
        this.stampCount++;
        this.totalStampAmount += preProcurement ? 0 : amt;
        this.totalStampAmountForPopup += amt;
      }
    });
    this.totalStampAmount = Math.round(this.totalStampAmount * 100) / 100;
    this.totalStampAmountForPopup =
      Math.round(this.totalStampAmountForPopup * 100) / 100;
  }

  /** Set a single fatal validation error and bail out */
  private _setFatalError(msg: string) {
    this.consolidatedErrors = [{ displayText: msg, originalItem: null }];
    this.validationResult = {
      isValid: false,
      errors: [msg],
      fileNames: [],
      fileCount: 0,
      data: [],
    };
    this.messageService.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: msg,
    });
  }

  /** Read the raw XLSX file and return JSON rows */
  private readExcelFile(file: File): Promise<any[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const wb = XLSX.read(e.target.result, {
            type: 'binary',
            cellFormula: false,
            cellDates: true,
          });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const json = XLSX.utils.sheet_to_json(ws, {
            header: 1,
            raw: true,
            defval: '',
            dateNF: 'dd-mm-yyyy',
          }) as any[][];

          if (!json.length) {
            reject(new Error('Excel file is empty.'));
            return;
          }

          const headers = json.shift() as string[];
          if (!headers?.length) {
            reject(new Error('Excel file has no headers.'));
            return;
          }

          const data = json
            .filter(
              (row) =>
                !row.every(
                  (c: any) =>
                    c === null ||
                    c === undefined ||
                    c === '' ||
                    (typeof c === 'string' && c.trim() === ''),
                ),
            )
            .map((row) => {
              const obj: any = {};
              headers.forEach((h, i) => {
                let v = row[i];
                if (v === undefined || v === null || v === '') {
                  obj[h] = '';
                  return;
                }
                if (v instanceof Date) {
                  const dd = String(v.getDate()).padStart(2, '0');
                  const mm = String(v.getMonth() + 1).padStart(2, '0');
                  obj[h] = `${dd}-${mm}-${v.getFullYear()}`;
                } else {
                  obj[h] = typeof v === 'string' ? v.trim() : v;
                }
              });
              return obj;
            });

          if (!data.length) {
            reject(new Error('No valid data rows found.'));
            return;
          }
          resolve(data);
        } catch {
          reject(new Error('Failed to parse Excel file.'));
        }
      };
      reader.onerror = () => reject(new Error('Failed to read Excel file.'));
      reader.readAsBinaryString(file);
    });
  }

  private _callPresignedUrlApi(
    payload: PresignedUrlRequest,
    sessionUuid: string,
  ): Promise<PresignedUrlApiResponse> {
    return new Promise((resolve, reject) => {
      this.estampService.getPresignedUrls(sessionUuid, payload).subscribe({
        next: (res) => resolve(res as PresignedUrlApiResponse),
        error: (err) => reject(err),
      });
    });
  }

  private _transformApiResponse(api: PresignedUrlApiResponse): {
    session_id: string;
    presigned_urls: TransformedPresignedUrl[];
  } {
    return {
      session_id: api.bulk_order_uuid,
      presigned_urls: api.urls.map((u) => ({
        file_name: u.file_name,
        upload_url: u.url.url,
        upload_fields: u.url.fields,
      })),
    };
  }

  private _generateSessionUuid(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';

    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    return result;
  }

  getTotalStampAmount() {
    return this.totalStampAmountForPopup.toFixed(2);
  }

  getBranchCode(): string {
    return this.validationResult?.data?.[0]?.branch_code ?? '';
  }

  getRequestedBy(): string {
    return this.storageService.getUser() ?? '';
  }

  viewIssues(content: any) {
    this.modalService
      .open(content, { size: 'lg', ariaLabelledBy: 'modal-basic-title' })
      .result.then(
        () => {},
        () => {},
      );
  }

  viewUploadedExcel(content: TemplateRef<any>) {
    this.modalService.open(content, {
      size: 'xl',
      centered: true,
      scrollable: true,
      modalDialogClass: 'largeTableModal',
    });
  }

  onStampProcessTypeChange(event: any) {
    this.selectedStampProcessType = event.value;
    if (this.uploadedExcelFile) this.validateExcel();
  }

  toggleTemplateDropdown() {
    this.showTemplateDropdown = !this.showTemplateDropdown;
  }

  downloadTemplate(type?: 'eStamp' | 'eSign' | 'both') {
    this.showTemplateDropdown = false;
    const serviceType = type || this.selectedServiceType;

    if (!serviceType) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Select Service Type',
        detail: 'Please select a service type first.',
      });
      return;
    }

    const fileMap = {
      eStamp: 'eStamp_Bulk_Upload_Template.xlsx',
      eSign: 'eSign_Bulk_Upload_Template.xlsx',
      both: 'Both_Bulk_Upload_Template.xlsx',
    };

    this._downloadFromAsset(fileMap[serviceType]);
  }

  private _downloadFromAsset(fileName: string) {
    const link = document.createElement('a');
    link.href = `assets/templates/${fileName}`;
    link.download = fileName;
    link.click();

    this.messageService.add({
      severity: 'success',
      summary: 'Template Downloaded',
      detail: `${fileName} has been downloaded.`,
    });
  }

  closeTemplateDropdown() {
    this.showTemplateDropdown = false;
  }

  // ── FIX: compute once and cache, never called by Angular CD ──
  private _updateErrorTableColumns(): void {
    const common = [
      { header: 'Reference ID', field: 'referance_id' },
      { header: 'Branch Code', field: 'branch_code' },
    ];

    const estampCols = [
      { header: 'First Party', field: 'first_party_name' },
      { header: 'Second Party', field: 'second_party_name' },
      { header: 'Stamp Duty Paid By', field: 'stamp_duty_paid_by' },
      { header: 'Stamp Duty Amount', field: 'stamp_duty_amount' },
      { header: 'Description', field: 'description' },
      { header: 'Article Id', field: 'article_code' },
      { header: 'Consideration Amount', field: 'consideration_amount' },
    ];

    const esignCols = [
      { header: 'Name', field: 'name' },
      { header: 'Email', field: 'email' },
      { header: 'Contact Number', field: 'contact_number' },
      { header: 'Sign Position', field: 'sign_position' },
      { header: 'Method', field: 'method' },
      { header: 'Pages', field: 'pages' },
      { header: 'Sign Mode', field: 'sign_mode' },
      { header: 'Aadhaar Digits', field: 'aadhaar_digits' },
    ];

    if (this.selectedServiceType === 'eStamp') {
      this.errorTableColumns = [...common, ...estampCols];
    } else if (this.selectedServiceType === 'eSign') {
      this.errorTableColumns = [...common, ...esignCols];
    } else {
      this.errorTableColumns = [...common, ...estampCols, ...esignCols];
    }
  }
}